import React from 'react';
import { useSelector } from 'react-redux';
import { Heading, Text } from '../../../../components/Typography';
import styles from './ContextWindowStats.module.scss';
import { RootState } from '../../../../store';

const ContextWindowStats: React.FC = () => {
  const { selectedDocuments, sectionCharacters, prompt } = useSelector(
    (state: RootState) => state.summaryTest,
  );

  const documentCount = Object.keys(selectedDocuments).length;
  const sectionCount = Object.values(selectedDocuments).reduce(
    (acc, doc) => acc + (doc.selectedSectionIdx?.length ?? 0),
    0,
  );
  const promptCharacters = prompt.length;
  const maxTokens = 1000000; // Default to Gemini's context window

  const sectionTokens = Math.ceil(sectionCharacters / 3);
  const promptTokens = Math.ceil(promptCharacters / 3);
  const totalTokens = sectionTokens + promptTokens;
  const tokenUtilization = (totalTokens / maxTokens) * 100;

  const getStatusClass = (percentage: number) => {
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return '';
  };

  const getStatusText = (percentage: number) => {
    if (percentage >= 90) return 'High usage - consider reducing content';
    if (percentage >= 70) return 'Moderate usage - monitor closely';
    return 'Normal usage';
  };

  const statusClass = getStatusClass(tokenUtilization);
  const statusText = getStatusText(tokenUtilization);

  return (
    <div className={`${styles.container} ${styles.sticky}`}>
      <Heading level={4} className={styles.header}>
        Context Window Stats
      </Heading>

      <div className={styles.stats}>
        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Documents:
          </Text>
          <Text weight="semibold" className={styles.statValue}>
            {documentCount}
          </Text>
        </div>

        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Sections:
          </Text>
          <Text weight="semibold" className={styles.statValue}>
            {sectionCount}
          </Text>
        </div>

        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Content Chars:
          </Text>
          <Text weight="semibold" className={styles.statValue}>
            {sectionCharacters.toLocaleString()}
          </Text>
        </div>

        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Content Tokens:
          </Text>
          <Text
            weight="semibold"
            className={`${styles.statValue} ${statusClass ? styles[statusClass + 'Value'] : ''}`}
          >
            {sectionTokens.toLocaleString()}
          </Text>
        </div>

        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Prompt Tokens:
          </Text>
          <Text weight="semibold" className={styles.statValue}>
            {promptTokens.toLocaleString()}
          </Text>
        </div>

        <div className={styles.statItem}>
          <Text size="small" color="neutral-600" className={styles.statLabel}>
            Total Usage:
          </Text>
          <Text
            weight="semibold"
            className={`${styles.statValue} ${statusClass ? styles[statusClass + 'Value'] : ''}`}
          >
            {tokenUtilization.toFixed(1)}%
          </Text>
        </div>
      </div>

      <div className={styles.progressBar}>
        <div
          className={`${styles.progressFill} ${statusClass ? styles[statusClass] : ''}`}
          style={{ width: `${Math.min(tokenUtilization, 100)}%` }}
        />
      </div>

      <div className={styles.summary}>
        <Text size="small" weight="semibold">
          Status:
        </Text>{' '}
        <Text size="small" color="neutral-600">
          {statusText}
        </Text>
      </div>
    </div>
  );
};

export default ContextWindowStats;
